import { useTranslations } from 'next-intl';
import { useRef } from 'react';
import { useFormContext } from 'react-hook-form';

import type { GeneralFormProps } from '@/types/general';

import { Button } from '../ui/button';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../ui/form';
import { Image } from '../ui/image';
import { Input } from '../ui/input';

const FallbackAvatar = '/images/logos/logo-round.png';

type Props = GeneralFormProps & {
  /**
   * Canonical helper text shown below the field.
   * Falls back to `description` for backward compatibility.
   */
  helperText?: string;
};

function RHFShadcnAvatarUploader({
  name,
  title,
  description,
  helperText,
  value: externalValue,
  onChange: externalOnChange,
  required,
  disabled,
}: Props) {
  const { control } = useFormContext();
  const resolvedHelperText = helperText ?? description;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const translate = useTranslations();

  const handleChangeClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const handleFileChange = (file: File) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64String = reader.result as string;

            const newEvent = { target: { value: base64String } };

            if (externalOnChange) {
              externalOnChange(newEvent);
            } else {
              field.onChange(newEvent);
            }
          };
          reader.readAsDataURL(file);
        };

        const avatarSrc
          = (field.value as string)
            || (externalValue as string)
            || FallbackAvatar;

        return (
          <FormItem className="w-full">
            {title !== undefined && title !== '' && (
              <FormLabel className="mb-1.5 block type-label-l leading-none">
                {title}
                {required === true && <span className="ml-0.5 text-error">*</span>}
              </FormLabel>
            )}
            <div className="flex flex-col items-start gap-3">
              <Image
                src={avatarSrc}
                alt=""
                className="size-20 rounded-full border border-border object-cover"
              />
              <FormControl>
                <Input
                  type="file"
                  id={name}
                  accept="image/*"
                  required={required}
                  disabled={disabled}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file !== undefined) {
                      handleFileChange(file);
                    }
                  }}
                  ref={fileInputRef}
                  className="hidden"
                />
              </FormControl>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleChangeClick}
                disabled={disabled}
              >
                {translate('uploads.uploader.browserFilesHint')}
              </Button>
            </div>
            {fieldState.error === undefined && resolvedHelperText !== undefined && resolvedHelperText !== '' && (
              <FormDescription className="mt-1.5 type-body-s text-on-surface-variant">
                {resolvedHelperText}
              </FormDescription>
            )}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}

export default RHFShadcnAvatarUploader;
